import { useState, useCallback } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { orderApi, OrderSearchQuery, OrderSearchView } from '@/entities/order';

export interface UseOrderSearchReturn {
  query: OrderSearchQuery | null;
  data: OrderSearchView | null;
  loading: boolean;
  error: Error | null;
  search: (searchQuery: OrderSearchQuery) => Promise<void>;
  setQuery: (query: OrderSearchQuery) => void;
}

const defaultQuery: OrderSearchQuery = {
  filterGroups: [],
  sortBy: 'orderedAt',
  sortOrder: 'DESC',
  pageSize: 20,
};

const DEDUPING_INTERVAL_MS = 500;

function createSearchKey(query: OrderSearchQuery): string {
  return JSON.stringify(query);
}

export function useOrderSearch(initialQuery?: OrderSearchQuery): UseOrderSearchReturn {
  const { mutate } = useSWRConfig();
  const [query, setQueryState] = useState<OrderSearchQuery | null>(initialQuery ?? defaultQuery);
  const [searchKey, setSearchKey] = useState<string | null>(null);

  const { data, error, isLoading } = useSWR<OrderSearchView>(
    searchKey ? ['orders-search', searchKey] : null,
    () => orderApi.searchOrders(JSON.parse(searchKey!) as OrderSearchQuery),
    {
      dedupingInterval: DEDUPING_INTERVAL_MS,
      keepPreviousData: true,
    }
  );

  const search = useCallback(
    async (searchQuery: OrderSearchQuery): Promise<void> => {
      const key = createSearchKey(searchQuery);
      setSearchKey(key);
      setQueryState(searchQuery);
      await mutate(['orders-search', key], () => orderApi.searchOrders(searchQuery), {
        revalidate: true,
      });
    },
    [mutate]
  );

  const setQuery = useCallback((q: OrderSearchQuery) => {
    setQueryState(q);
  }, []);

  return {
    query,
    data: data ?? null,
    loading: isLoading,
    error: error
      ? error instanceof Error
        ? error
        : new Error('검색 중 오류가 발생했습니다.')
      : null,
    search,
    setQuery,
  };
}
